import { useEffect, useState } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Activity, Plus, Loader2, Trash2, Wand2, Calendar,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate, cn } from '../../lib/utils';

const STATUSES = [
  { value: 'draft', label: 'Rascunho', color: 'bg-slate-100 text-slate-700' },
  { value: 'submitted', label: 'Submetido', color: 'bg-blue-50 text-blue-700' },
  { value: 'closed', label: 'Encerrado', color: 'bg-green-50 text-green-700' },
];

const init = {
  system_id: '', period_start: '', period_end: '',
  incidents_count: 0, near_miss_count: 0, user_complaints: 0,
  performance_drift: '', accuracy_degradation: '',
  corrective_actions: '', model_updates: '', next_review_date: '',
  status: 'draft',
};

export default function AiPostMarket() {
  const [reports, setReports] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [aggregating, setAggregating] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([aiActApi.listPostMarket({}), aiActApi.listSystems({})]);
      setReports(r.data); setSystems(s.data);
    } catch { toast.error('Erro ao carregar relatórios post-market'); }
    finally { setLoading(false); }
  };

  const handleAutoAggregate = async () => {
    if (!form.system_id) { toast.error('Selecione um sistema primeiro'); return; }
    setAggregating(true);
    try {
      const res = await aiActApi.autoAggregatePostMarket(form.system_id, 90);
      setForm({
        ...form,
        period_start: res.data.period_start,
        period_end: res.data.period_end,
        incidents_count: res.data.incidents_count,
        near_miss_count: res.data.near_miss_count,
      });
      toast.success('Métricas auto-agregadas dos últimos 90 dias');
    } catch { toast.error('Erro na auto-agregação'); }
    finally { setAggregating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.system_id || !form.period_start || !form.period_end) {
      toast.error('Sistema e período obrigatórios'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidents_count: Number(form.incidents_count) || 0,
        near_miss_count: Number(form.near_miss_count) || 0,
        user_complaints: Number(form.user_complaints) || 0,
        next_review_date: form.next_review_date || null,
      };
      await aiActApi.createPostMarket(payload);
      toast.success('Relatório post-market criado');
      setShowForm(false); setForm(init); load();
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!delTarget) return;
    try {
      await aiActApi.deletePostMarket(delTarget.report_id);
      toast.success('Relatório eliminado');
      setDelTarget(null); load();
    } catch { toast.error('Erro ao eliminar'); }
  };

  const getSysName = (sid) => systems.find(s => s.system_id === sid)?.name || sid;

  if (loading) return <Layout title="EU AI Act — Post-Market"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;

  return (
    <Layout title="EU AI Act — Post-Market Surveillance (F13)" headerContent={
      <Button onClick={() => { setForm(init); setShowForm(true); }} data-testid="new-postmarket-btn">
        <Plus className="h-4 w-4 mr-2" />Novo Relatório
      </Button>
    }>
      <div className="space-y-6" data-testid="postmarket-container">

        <Card className="card-flat bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Activity className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Vigilância Pós-Comercialização</p>
              <p className="text-sm text-blue-700 mt-1">
                Os fornecedores de sistemas IA de alto risco devem monitorizar o desempenho durante o seu ciclo de vida (Art. 72).
                Crie relatórios periódicos (trimestrais/semestrais) com métricas de incidentes, complaints e drift.
              </p>
            </div>
          </CardContent>
        </Card>

        {reports.length === 0 ? (
          <Card className="card-flat"><CardContent className="p-8 text-center">
            <Activity className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum relatório post-market</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="postmarket-list">
            {reports.map(r => {
              const sCfg = STATUSES.find(s => s.value === r.status) || STATUSES[0];
              return (
                <Card key={r.report_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`pmr-${r.report_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                        <Activity className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-xs text-slate-400">{r.report_id}</span>
                          <Badge variant="outline" className={cn('text-xs', sCfg.color)}>{sCfg.label}</Badge>
                          <Badge variant="outline" className="text-xs">{getSysName(r.system_id)}</Badge>
                        </div>
                        <p className="text-sm font-medium text-slate-900">
                          Período: {formatDate(r.period_start)} → {formatDate(r.period_end)}
                        </p>
                        <div className="grid grid-cols-3 gap-4 mt-2 text-xs text-slate-600">
                          <span>Incidentes: <strong>{r.incidents_count}</strong></span>
                          <span>Near-miss: <strong>{r.near_miss_count}</strong></span>
                          <span>Complaints: <strong>{r.user_complaints}</strong></span>
                        </div>
                        {r.corrective_actions && (
                          <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                            <strong>Ações:</strong> {r.corrective_actions}
                          </p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDelTarget(r)} data-testid={`delete-pmr-${r.report_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Relatório Post-Market</DialogTitle>
            <DialogDescription>Vigilância pós-comercialização (Art. 72)</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Sistema IA *</Label>
              <Select value={form.system_id} onValueChange={v => setForm({ ...form, system_id: v })}>
                <SelectTrigger data-testid="pmr-system"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{systems.map(s => <SelectItem key={s.system_id} value={s.system_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={handleAutoAggregate} disabled={aggregating || !form.system_id} data-testid="auto-aggregate-btn">
              {aggregating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
              Auto-agregar últimos 90 dias
            </Button>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Início período *</Label>
                <Input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Fim período *</Label>
                <Input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Incidentes graves</Label>
                <Input type="number" min="0" value={form.incidents_count} onChange={e => setForm({ ...form, incidents_count: e.target.value })} data-testid="pmr-incidents" />
              </div>
              <div className="space-y-2">
                <Label>Near-miss</Label>
                <Input type="number" min="0" value={form.near_miss_count} onChange={e => setForm({ ...form, near_miss_count: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Complaints</Label>
                <Input type="number" min="0" value={form.user_complaints} onChange={e => setForm({ ...form, user_complaints: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Performance drift</Label>
              <Textarea rows={2} value={form.performance_drift} onChange={e => setForm({ ...form, performance_drift: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Degradação de exatidão</Label>
              <Textarea rows={2} value={form.accuracy_degradation} onChange={e => setForm({ ...form, accuracy_degradation: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Ações corretivas</Label>
              <Textarea rows={3} value={form.corrective_actions} onChange={e => setForm({ ...form, corrective_actions: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Atualizações ao modelo</Label>
              <Textarea rows={2} value={form.model_updates} onChange={e => setForm({ ...form, model_updates: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Próxima revisão</Label>
                <Input type="date" value={form.next_review_date} onChange={e => setForm({ ...form, next_review_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-pmr-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={v => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Relatório</AlertDialogTitle>
            <AlertDialogDescription>Confirma a eliminação de {delTarget?.report_id}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-pmr">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
